import { MessageSquare } from 'lucide-react';
import SolutionPage from '../../components/SolutionPage';

export default function CustomerCommunication() {
  return (
    <SolutionPage
      label="Solution"
      headline="Customers who cannot get answers quickly often move on silently."
      subheadline="Speed and clarity in communication are not just conveniences. They are often the deciding factor in whether a customer chooses your business or someone else."
      heroImage="https://images.pexels.com/photos/3184360/pexels-photo-3184360.jpeg?auto=compress&cs=tinysrgb&w=1920"
      icon={MessageSquare}
      problemTitle="Slow or unclear responses quietly damage customer relationships."
      problemBody={[
        'Most customers do not complain when they cannot get a quick answer. They simply move to the next option. That loss is invisible, but it happens consistently when there is no reliable process for handling incoming inquiries.',
        'For many businesses, the challenge is not unwillingness to communicate. It is the absence of a reliable process that handles inquiries consistently, especially outside of normal working hours.',
        'A customer who reaches out after hours, on a weekend, or during a busy period and receives no response has already formed an impression of the business, and it is often not a favorable one.',
      ]}
      outcomes={[
        {
          title: 'Faster response to incoming inquiries',
          description:
            'Customers receive timely acknowledgment and useful information without requiring a staff member to be immediately available.',
        },
        {
          title: 'Consistent communication quality',
          description:
            'Every customer receives the same standard of response, regardless of time of day, staff availability, or how busy the business is.',
        },
        {
          title: 'Reduced friction for customers',
          description:
            'When communication is easy and reliable, customers feel more confident in the business and more comfortable proceeding.',
        },
        {
          title: 'Better use of staff time',
          description:
            'When routine communication is handled consistently, your team can focus on the conversations that genuinely require their attention.'
        },
      ]}
      whatWeDoTitle="We design communication workflows that keep customers informed and engaged."
      whatWeDoBody={[
        'Good customer communication is not just about being available. It is about being consistent, clear, and helpful at the moments that matter.',
        'We design and implement clear processes that handle routine inquiries reliably, capture important information from potential customers, and ensure nothing falls through the gaps.',
        'The goal is not to replace human interaction. It is to ensure that every customer receives a timely, professional response, and that your team is equipped with the right information when a conversation does require their direct involvement.'
      ]}
      whatYouGain={[
        'Fewer missed inquiries, including those outside business hours.',
        'A more consistent customer experience regardless of staff availability.',
        'Reduced burden on your team for routine communications.',
        'Customers who feel informed and respected throughout the process.',
        'Better visibility into what customers are asking and what they need.',
        'More confidence that no important conversation is falling through the gaps.',
      ]}
      relatedSolutions={[
        { title: 'Appointment Coordination', href: '/solutions/appointment-coordination' },
        { title: 'Lead Qualification', href: '/solutions/lead-qualification' },
        { title: 'Business Workflow Support', href: '/solutions/business-workflow' },
      ]}
    />
  );
}
